import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, FullColumn, useOverlayContext } from 'seagull';
import { useIoContext } from '~/providers/IoContext';
import { useStoreActions } from '~/store/store-context';
import DrawerHeader from './DrawerHeader';
import styles from './HelpMeWriteDrawer.module.scss';
import { emptyResponseData, GetHelpMeWriteDrawerTID } from './HelpMeWriteDrawer.utils';
import HelpMeWriteTextField from './HelpMeWriteTextField/HelpMeWriteTextField';
import SuggestedOutputDisplay from './SuggestedOutputDisplay/SuggestedOutputDisplay';
const HelpMeWriteDrawer = (param)=>{
    let { open, onClose, drawerWidth = 564, presets, suggestions, mutations, outputRenderer, biEventIds, texts, hasExistingContent, onDrawerOpen, renderFeedbackModal, shouldShowFeedbackOnClose } = param;
    const { HelpMeWriteDrawerTID } = useMemo(()=>GetHelpMeWriteDrawerTID(), []);
    const overlayContext = useOverlayContext();
    const { logBI } = useStoreActions();
    const { intercomClient } = useIoContext();
    const [textPrompt, setTextPrompt] = useState('');
    const [response, setResponse] = useState(emptyResponseData);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isUndoing, setIsUndoing] = useState(false);
    const [isInserting, setIsInserting] = useState(false);
    const hasTriggeredOpenRef = useRef(false);
    const hasInteractedRef = useRef(false);
    const hasAcceptedRef = useRef(false);
    const { canUndo, isClarification, conversationId, output, clarificationText } = response;
    const hasConversationStarted = conversationId !== '';
    const isEmpty = !hasConversationStarted && output == null && clarificationText == null;
    const handleClose = ()=>{
        onClose();
        setTextPrompt('');
        setResponse(emptyResponseData());
        setIsGenerating(false);
        setIsUndoing(false);
        setIsInserting(false);
        hasInteractedRef.current = false;
        hasAcceptedRef.current = false;
        hasTriggeredOpenRef.current = false;
    };
    const closeAndReject = ()=>{
        const hasInteracted = hasInteractedRef.current;
        const hasAccepted = hasAcceptedRef.current;
        const showFeedback = renderFeedbackModal != null && (shouldShowFeedbackOnClose == null ? !isEmpty && !hasAccepted : shouldShowFeedbackOnClose({
            hasInteracted,
            hasAccepted
        }));
        const conversationIdAtClose = conversationId;
        logBI(biEventIds.closeClick);
        handleClose();
        if (!isEmpty && hasConversationStarted) {
            void mutations.onReject(conversationIdAtClose);
        }
        if (showFeedback && hasConversationStarted) {
            overlayContext.showModal({
                biName: 'feedback_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(_Fragment, {
                        children: renderFeedbackModal({
                            conversationId: conversationIdAtClose,
                            onClose: dismissModal
                        })
                    });
                }
            });
        }
    };
    useEffect(()=>{
        if (open) {
            intercomClient.hideIntercom();
        } else {
            intercomClient.showIntercom();
        }
    }, [
        open,
        intercomClient
    ]);
    const conversationIdRef = useRef('');
    const onRejectRef = useRef(mutations.onReject);
    useEffect(()=>{
        conversationIdRef.current = conversationId;
        onRejectRef.current = mutations.onReject;
    });
    useEffect(()=>()=>{
            const id = conversationIdRef.current;
            if (id !== '' && !hasAcceptedRef.current) {
                void onRejectRef.current(id);
            }
        }, // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    []);
    useEffect(()=>{
        if (!open) {
            hasTriggeredOpenRef.current = false;
            setResponse(emptyResponseData());
            setTextPrompt('');
            setIsGenerating(false);
            setIsUndoing(false);
            setIsInserting(false);
            hasInteractedRef.current = false;
            hasAcceptedRef.current = false;
            return;
        }
        if (hasTriggeredOpenRef.current) return;
        hasTriggeredOpenRef.current = true;
        if (hasExistingContent) {
            setResponse(_object_spread_props(_object_spread({}, emptyResponseData()), {
                clarificationText: texts.promptQuestion,
                isClarification: true
            }));
        }
        if (onDrawerOpen) {
            setIsGenerating(true);
            Promise.resolve(onDrawerOpen(setResponse)).catch(()=>setErrorResponse()).finally(()=>setIsGenerating(false));
        }
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        open
    ]);
    const setErrorResponse = ()=>{
        setResponse(_object_spread_props(_object_spread({}, emptyResponseData()), {
            isClarification: true,
            clarificationText: texts.errorMessage
        }));
    };
    const sendMessage = async (prompt)=>{
        if (isEmpty) return;
        hasInteractedRef.current = true;
        setIsGenerating(true);
        try {
            const next = await mutations.onMessage(conversationId, prompt);
            setResponse(next);
        } catch (unused) {
            setErrorResponse();
        } finally{
            setIsGenerating(false);
        }
    };
    const handleSubmit = async ()=>{
        if (!textPrompt.trim()) return;
        await sendMessage({
            kind: 'freeText',
            text: textPrompt
        });
    };
    const handlePresetClick = async (preset)=>{
        await sendMessage({
            kind: 'preset',
            preset
        });
    };
    const handleInsert = async ()=>{
        if (output == null || !hasConversationStarted) return;
        hasInteractedRef.current = true;
        hasAcceptedRef.current = true;
        setIsInserting(true);
        logBI(hasExistingContent ? biEventIds.replaceClick : biEventIds.insertClick);
        try {
            await mutations.onAccept(conversationId, output);
            setTimeout(()=>handleClose(), 2000);
        } catch (unused) {
            setIsInserting(false);
            setErrorResponse();
        }
    };
    const handleUndo = async ()=>{
        if (!hasConversationStarted) return;
        logBI(biEventIds.undoClick);
        setIsUndoing(true);
        try {
            const next = await mutations.onUndo(conversationId);
            setResponse(next);
        } catch (unused) {
            setErrorResponse();
        } finally{
            setIsUndoing(false);
        }
    };
    const showLoading = isGenerating;
    const textFieldDisabled = isGenerating || isUndoing || isInserting || isEmpty;
    const textFieldIsLoading = isGenerating || isEmpty;
    return /*#__PURE__*/ _jsx(Drawer, {
        bi: {
            name: 'help_me_write_drawer'
        },
        variant: "persistent",
        minWidth: drawerWidth - 4,
        open: open,
        onClose: closeAndReject,
        onCloseButtonClick: closeAndReject,
        disableCloseButton: isInserting,
        PaperProps: _object_spread_props(_object_spread({}, {
            'data-testid': HelpMeWriteDrawerTID.Paper()
        }), {
            className: styles.drawerPaper,
            style: {
                width: drawerWidth
            }
        }),
        showDividers: false,
        header: /*#__PURE__*/ _jsx(DrawerHeader, {
            title: texts.headerTitle,
            beta: texts.headerBeta
        }),
        testId: HelpMeWriteDrawerTID.Root(),
        children: /*#__PURE__*/ _jsxs(FullColumn, {
            gap: 16,
            padding: 16,
            justifyContent: "space-between",
            testId: HelpMeWriteDrawerTID.Content(),
            children: [
                /*#__PURE__*/ _jsx(SuggestedOutputDisplay, {
                    isLoading: showLoading,
                    isUndoLoading: isUndoing,
                    output: output,
                    clarificationText: clarificationText,
                    isClarification: isClarification,
                    canUndo: canUndo,
                    hasExistingContent: hasExistingContent,
                    isInserting: isInserting,
                    outputRenderer: outputRenderer,
                    texts: texts,
                    onInsert: handleInsert,
                    onUndo: handleUndo
                }),
                /*#__PURE__*/ _jsx(HelpMeWriteTextField, {
                    conversationId: conversationId,
                    value: textPrompt,
                    onChange: setTextPrompt,
                    onSubmit: handleSubmit,
                    onPresetClick: handlePresetClick,
                    isLoading: textFieldIsLoading,
                    disabled: textFieldDisabled,
                    isUndoLoading: isUndoing,
                    presets: presets,
                    suggestions: suggestions,
                    placeholder: hasConversationStarted ? texts.placeholderAfterStart : texts.placeholder,
                    biEventIds: biEventIds
                })
            ]
        })
    });
};
export default HelpMeWriteDrawer;
